/** Shared WebSocket base URL for live inbox / logs. */
export const getWsBaseUrl = () => {
    if (process.env.REACT_APP_WS_URL) {
        return process.env.REACT_APP_WS_URL.replace(/\/$/, "");
    }

    const api = process.env.REACT_APP_API_URL?.trim();
    if (api) {
        return api
            .replace(/\/api\/?$/, "")
            .replace(/^http/i, (m) => (m.toLowerCase() === "https" ? "wss" : "ws"));
    }

    if (typeof window !== "undefined" && window.location?.host) {
        const proto = window.location.protocol === "https:" ? "wss" : "ws";
        return `${proto}://${window.location.host}`;
    }

    return "ws://localhost:8000";
};

/** Backend origin for media (never the React :3000 host). */
const getMediaOrigin = () => {
    const file = (process.env.REACT_APP_FILE_URL || "").trim().replace(/\/$/, "");
    if (file) return file.replace(/\/media\/?$/, "");

    const api = (process.env.REACT_APP_API_URL || "").trim();
    if (api) return api.replace(/\/api\/?$/, "").replace(/\/$/, "");

    if (typeof window !== "undefined" && window.location?.hostname === "localhost") {
        return "http://localhost:8000";
    }
    return "";
};

/**
 * Resolve media for the current env.
 * Always rewrite /media/... (even absolute ngrok/prod URLs) to the API origin
 * so localhost React loads from :8000, not :3000 or a stale PUBLIC_URI tunnel.
 */
export const mediaUrl = (u) => {
    if (!u) return "";
    const raw = String(u).trim();
    if (!raw) return "";
    if (raw.startsWith("blob:") || raw.startsWith("data:")) return raw;

    const origin = getMediaOrigin();
    const fileBase = origin ? `${origin}/media` : (process.env.REACT_APP_FILE_URL || "").replace(/\/$/, "");

    // Absolute or relative URL that contains /media/...
    const mediaPathMatch = raw.match(/\/media\/[^?\s#]+/i);
    if (mediaPathMatch) {
        const mediaPath = mediaPathMatch[0];
        return origin ? `${origin}${mediaPath}` : mediaPath;
    }

    // Relative path like avatars/x.jpg or chat/x.webp
    if (!/^https?:\/\//i.test(raw)) {
        const path = raw.replace(/^\/+/, "");
        if (fileBase) return `${fileBase}/${path}`;
        return origin ? `${origin}/media/${path}` : `/media/${path}`;
    }

    // Non-media absolute URL (external) — leave as-is
    return raw;
};

export const normPhone = (v) => String(v || "").replace(/@.*$/, "").replace(/\D/g, "");

export const normJid = (v) => String(v || "").trim().toLowerCase();

export const isGroupJid = (v) => /@g\.us$/i.test(String(v || ""));

/** True for WhatsApp group conversations (flags or @g.us JID). */
export const isGroupConversation = (conv) => {
    if (!conv) return false;
    if (conv.is_group === true || conv.isGroup === true) return true;
    if (String(conv.chat_type || conv.type || "").toLowerCase() === "group") return true;
    return isGroupJid(conv.wa_jid) || isGroupJid(conv.sid) || isGroupJid(conv.jid);
};

/** Match conversation to inbound chat payload (chat_id first, then JID/phone). */
export const sameChat = (conv, msg) => {
    if (!conv || !msg) return false;
    if (msg.chat_id != null && String(conv.id) === String(msg.chat_id)) return true;

    const convJid = normJid(conv.wa_jid || conv.sid || conv.jid);
    const msgJid = normJid(msg.wa_jid || msg.sid || msg.jid || msg.to || msg.chatJid);
    if (convJid && msgJid && (isGroupJid(convJid) || isGroupJid(msgJid))) {
        return convJid === msgJid;
    }

    const a = normPhone(conv.sid) || normPhone(conv.wa_jid);
    const b = normPhone(msg.sid) || normPhone(msg.wa_jid) || normPhone(msg.from);
    return !!(a && b && a === b);
};
